import { useState } from "react";
import { Link, useLocation } from "wouter";
import { HelpCircle, LogOut, ClipboardList } from "lucide-react";
import { HelpPanel } from "@/components/help-panel";
import { NotificationBell } from "@/components/notification-bell";
import { ThemeToggle } from "@/components/theme-provider";
import { useUser } from "@/components/user-context";
import { OfflineBanner } from "@/components/offline-banner";

interface Props {
  children: React.ReactNode;
}

export function EngineerLayout({ children }: Props) {
  const { currentUser, logout } = useUser();
  const [location, navigate] = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);

  const extraItems = [
    {
      label: "Help",
      icon: HelpCircle,
      onClick: () => setHelpOpen(true),
      testId: "nav-engineer-help",
    },
    {
      label: "Sign out",
      icon: LogOut,
      onClick: () => { navigate("/"); logout(); },
      testId: "nav-engineer-signout",
    },
  ];

  const totalCols = 1 + extraItems.length;

  return (
    <div className="flex flex-col w-full overflow-hidden" style={{ height: "100dvh" }}>
      {/* Top header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-background shrink-0">
        <div className="flex items-center gap-2.5">
          {/* Logo mark — shield + tick, BS 5499 emergency-exit green */}
          <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-label="FireSafetyOps" className="shrink-0">
            <polygon points="4,5 28,5 28,18 16,30 4,18" fill="#00843D" />
            <polygon points="10,17 14.5,21.5 22,12 19.5,9.8 14.2,16.2 12,14" fill="#ffffff" />
          </svg>
          <div className="leading-tight">
            <span className="font-semibold text-sm tracking-tight">FireSafetyOps</span>
            <p className="text-xs text-muted-foreground">{currentUser?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <ThemeToggle />
        </div>
      </header>

      <OfflineBanner />

      <main className="flex-1 overflow-auto">
        {children}
      </main>

      <nav
        className="grid border-t border-border bg-background shrink-0"
        style={{
          gridTemplateColumns: `repeat(${totalCols}, 1fr)`,
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        aria-label="Main navigation"
      >
        <Link
          href="/"
          className={`flex flex-col items-center justify-center gap-1.5 py-3 min-h-[60px] w-full transition-colors ${
            location === "/" || location.startsWith("/visits") ? "text-primary" : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="nav-engineer-my-visits"
        >
          <ClipboardList className="w-5 h-5" />
          <span className="text-xs font-medium">My Visits</span>
        </Link>

        {extraItems.map(item => (
          <button
            key={item.label}
            type="button"
            onClick={item.onClick}
            className="flex flex-col items-center justify-center gap-1.5 py-3 min-h-[60px] w-full text-muted-foreground hover:text-foreground transition-colors"
            data-testid={item.testId}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs font-medium">{item.label}</span>
          </button>
        ))}
      </nav>

      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} role="engineer" />
    </div>
  );
}
